import { LegalEntityParty } from '@types'

/**
 * List of legal entity types that require a trustee name.
 * These are trust-type entities from both ABN and ACN lookups.
 */
export const TRUST_ENTITY_TYPES = [
  'Discretionary Trading Trust',
  'Discretionary Investment Trust',
  'Discretionary Services Management Trust',
  'Discretionary Trust',
  'Fixed Trust',
  'Fixed Unit Trust',
  'Hybrid Trust',
  'Unit Trust',
  'Public Unit Trust',
  'Cash Management Trust',
  'Family Trust',
  'Testamentary Trust',
  'Superannuation Fund',
  'SMSF',
  'Self Managed Superannuation Fund',
  'Approved Deposit Fund',
  'Pooled Superannuation Trust',
  'Charitable Trust',
  // Add more trust types as needed
] as const

/**
 * Check if an entity type requires a trustee name.
 * @param legalEntityType - The entity type from ABN/ACN lookup
 * @returns true if the entity is a trust type requiring a trustee
 */
export const isTrustEntityType = (legalEntityType?: string): boolean => {
  if (!legalEntityType) return false
  const normalizedType = legalEntityType.toLowerCase().trim()
  return TRUST_ENTITY_TYPES.some(
    (trustType) => normalizedType === trustType.toLowerCase()
  )
}

/**
 * Check if an entity requires a trustee name based on handling instructions from the backend.
 * The platform returns NEEDS_TRUSTEE stipulation for trusts that require a trustee name.
 * @param entity - The legal entity party
 * @returns true if the entity needs a trustee name to be provided
 */
export const entityNeedsTrusteeName = (entity?: LegalEntityParty | null): boolean => {
  if (!entity) return false
  
  // Check if backend explicitly indicates NEEDS_TRUSTEE
  if (entity.handlingInstructions?.stipulations === 'NEEDS_TRUSTEE') {
    return true
  }
  
  // Fallback: Check entity type for trust types (for entities without handlingInstructions)
  if (isTrustEntityType(entity.legalEntityType)) {
    return true
  }
  
  return false
}

/**
 * Check if the entity name already has a trustee prefix.
 * @param commonName - The entity's common name
 * @returns true if the name already has "THE TRUSTEE FOR" prefix
 */
export const hasTrusteePrefix = (commonName?: string): boolean => {
  if (!commonName) return false
  return commonName.toUpperCase().trim().startsWith('THE TRUSTEE FOR')
}

/**
 * Get the trustee name from an entity.
 * Checks multiple sources: requestedTrustee, handlingInstructions.trusteeName
 * @param entity - The legal entity party
 * @returns The trustee name if available
 */
export const getTrusteeName = (entity?: LegalEntityParty | null): string => {
  if (!entity) return ''
  
  // First check requestedTrustee (user-provided)
  if (entity.requestedTrustee?.trusteeName?.trim()) {
    return entity.requestedTrustee.trusteeName.trim()
  }
  
  // Then check handlingInstructions.trusteeName (from backend)
  if (entity.handlingInstructions?.trusteeName?.trim()) {
    return entity.handlingInstructions.trusteeName.trim()
  }
  
  return ''
}

/**
 * Generate the display name for a beneficiary, incorporating trustee name if applicable.
 * 
 * Rules:
 * - If handlingInstructions.displayName is provided by backend: use it directly
 * - If requestedTrustee.trusteeName is provided: "{trusteeName} {commonName}"
 * - If handlingInstructions.trusteeName exists: "{trusteeName} {commonName}"
 * - Otherwise: use commonName as-is
 * 
 * @param entity - The legal entity party (beneficiary)
 * @returns The display name to use in UI
 */
export const getBeneficiaryDisplayName = (entity?: LegalEntityParty | null): string => {
  if (!entity) return ''
  
  // If the platform provided a display name in handling instructions, use it directly
  if (entity.handlingInstructions?.displayName) {
    return entity.handlingInstructions.displayName
  }
  
  // Get trustee name from available sources
  const trusteeName = getTrusteeName(entity)
  
  // If trustee name exists, format it as "TrusteeName TrustName"
  if (trusteeName) {
    return `${trusteeName} ${entity.commonName || ''}`
  }
  
  // Default: use the common name
  return entity.commonName || ''
}

/**
 * Generate the full beneficiary name with ABN/ACN for certificate display.
 * Format: "{displayName} ABN {abn}" or "{displayName} ACN {acn}"
 * 
 * @param entity - The legal entity party (beneficiary)
 * @returns The full name with business identifier for certificate
 */
export const getBeneficiaryCertificateName = (entity?: LegalEntityParty | null): string => {
  if (!entity) return ''
  
  const displayName = getBeneficiaryDisplayName(entity)
  
  // Get the ABN/ACN code
  const codeEntry = entity.legalEntityCodes?.find(
    (c) => c.legalEntityCodeType === 'ABN' || c.legalEntityCodeType === 'ACN'
  )
  
  if (codeEntry?.legalEntityCodeData) {
    return `${displayName} ${codeEntry.legalEntityCodeType} ${codeEntry.legalEntityCodeData}`
  }
  
  return displayName
}

/**
 * Validate if a trustee name should be required for a given entity.
 * Returns an error message if required but not provided.
 * 
 * @param entity - The legal entity party
 * @returns Error message if validation fails, empty string if valid
 */
export const validateTrusteeName = (entity?: LegalEntityParty | null): string => {
  if (!entity) return ''
  
  // If entity needs trustee and none provided
  if (entityNeedsTrusteeName(entity) && !getTrusteeName(entity)) {
    return 'Trustee name is required for trust beneficiary'
  }
  
  return ''
}
